import React, { useState, useEffect, memo, useCallback } from "react";
import { Checkbox } from "../ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Edit,
  Trash2,
  Plus,
  Check,
  X,
  ArrowUpDown,
  Search,
  XCircle,
} from "lucide-react";

// Create a memoized row component to prevent unnecessary re-renders
const VariableRow = memo(
  ({
    variable,
    isSelected,
    onSelect,
    onEdit,
    onDelete,
    editingVar,
    editValue,
    startEditing,
    cancelEditing,
    saveEdit,
    setEditValue,
  }) => {
    // Handle keyboard events for the input field
    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        saveEdit(variable);
      } else if (e.key === "Escape") {
        cancelEditing();
      }
    };

    return (
      <TableRow className="h-8 hover:bg-muted/50 group border-b border-muted/30">
        <TableCell className="py-1 w-10 pl-4">
          <div className="flex items-center justify-center">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onSelect(variable.uuid)}
              className="data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground border-muted-foreground/30"
            />
          </div>
        </TableCell>
        <TableCell className="font-medium py-1 truncate text-sm">
          {variable.key}
        </TableCell>
        <TableCell className="py-1">
          {editingVar === variable.uuid ? (
            <div className="flex items-center space-x-1">
              <Input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="h-6 text-sm"
                autoFocus
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => saveEdit(variable)}
              >
                <Check className="h-3.5 w-3.5 text-green-600" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={cancelEditing}
              >
                <X className="h-3.5 w-3.5 text-red-600" />
              </Button>
            </div>
          ) : (
            <div
              className="cursor-pointer rounded text-sm px-2 py-0.5 -mx-2 hover:bg-muted transition-colors"
              onClick={() => !variable.secured && startEditing(variable)}
              title={
                variable.secured
                  ? "Secured values cannot be viewed"
                  : "Click to edit"
              }
            >
              {variable.secured ? "••••••••" : variable.value}
            </div>
          )}
        </TableCell>
        <TableCell className="py-1">
          {variable.secured ? (
            <span className="inline-flex items-center rounded-full bg-green-50 px-1.5 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
              Yes
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-gray-50 px-1.5 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
              No
            </span>
          )}
        </TableCell>
        <TableCell className="text-right py-1">
          <div className="flex justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => onEdit(variable)}
              title="Edit all properties"
            >
              <Edit className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => onDelete(variable.uuid)}
              title="Delete variable"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  }
);

VariableRow.displayName = "VariableRow";

const VariableTable = ({
  variables,
  selectedVariables,
  onSelectVariable,
  onSelectAll,
  onEditVariable,
  onDeleteVariable,
  onAddVariable,
}) => {
  const [editingVar, setEditingVar] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [filteredVariables, setFilteredVariables] = useState(variables);
  const [filter, setFilter] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "key",
    direction: "ascending",
  });

  // Memoize callbacks to prevent unnecessary re-renders
  const startEditing = useCallback((variable) => {
    setEditingVar(variable.uuid);
    setEditValue(variable.value || "");
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingVar(null);
  }, []);

  const saveEdit = useCallback(
    (variable) => {
      onEditVariable({
        ...variable,
        value: editValue,
        _inlineEdit: true,
      });
      setEditingVar(null);
    },
    [editValue, onEditVariable]
  );

  const clearFilter = useCallback(() => {
    setFilter("");
  }, []);

  // Update filtered variables when props or filter changes
  useEffect(() => {
    let result = [...variables];

    // Apply filter
    if (filter.trim()) {
      const lowercasedFilter = filter.toLowerCase();
      result = result.filter(
        (variable) =>
          variable.key.toLowerCase().includes(lowercasedFilter) ||
          (variable.value &&
            !variable.secured &&
            variable.value.toLowerCase().includes(lowercasedFilter))
      );
    }

    // Apply sort
    if (sortConfig.key) {
      result.sort((a, b) => {
        if (a[sortConfig.key] === null) return 1;
        if (b[sortConfig.key] === null) return -1;

        const aValue =
          typeof a[sortConfig.key] === "string"
            ? a[sortConfig.key].toLowerCase()
            : a[sortConfig.key];
        const bValue =
          typeof b[sortConfig.key] === "string"
            ? b[sortConfig.key].toLowerCase()
            : b[sortConfig.key];

        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredVariables(result);
  }, [variables, filter, sortConfig]);

  const requestSort = useCallback(
    (key) => {
      let direction = "ascending";
      if (sortConfig.key === key && sortConfig.direction === "ascending") {
        direction = "descending";
      }
      setSortConfig({ key, direction });
    },
    [sortConfig]
  );

  const getSortIndicator = useCallback(
    (key) => {
      if (sortConfig.key !== key) {
        return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
      }
      return sortConfig.direction === "ascending" ? (
        <ArrowUpDown className="h-3 w-3 ml-1 text-primary" />
      ) : (
        <ArrowUpDown className="h-3 w-3 ml-1 text-primary rotate-180" />
      );
    },
    [sortConfig]
  );

  if (variables.length === 0) {
    return (
      <div className="py-24 text-center">
        <p className="text-muted-foreground">No variables found</p>
        <Button
          variant="outline"
          size="sm"
          onClick={onAddVariable}
          className="mt-4"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add your first variable
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 relative">
        <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Filter variables by key or value..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="pl-8 h-8 text-sm"
        />
        {filter && (
          <Button
            variant="ghost"
            size="icon"
            onClick={clearFilter}
            className="absolute right-1 top-1 h-6 w-6"
          >
            <XCircle className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="overflow-x-auto">
        <Table className="table-fixed border-collapse">
          <TableHeader>
            <TableRow className="h-8 bg-muted/50">
              <TableHead className="w-10 font-medium text-xs py-1 pl-4">
                <div className="flex items-center justify-center">
                  <Checkbox
                    id="select-all"
                    checked={
                      filteredVariables.length > 0 &&
                      filteredVariables.every((v) =>
                        selectedVariables.includes(v.uuid)
                      )
                    }
                    onCheckedChange={(checked) => {
                      if (checked) {
                        onSelectAll(
                          true,
                          filteredVariables.map((v) => v.uuid)
                        );
                      } else {
                        onSelectAll(
                          false,
                          filteredVariables.map((v) => v.uuid)
                        );
                      }
                    }}
                    className="data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground border-muted-foreground/30"
                  />
                </div>
              </TableHead>
              <TableHead
                className="w-1/3 font-medium text-xs py-1 cursor-pointer hover:bg-muted/70"
                onClick={() => requestSort("key")}
              >
                <div className="flex items-center">
                  Key
                  {getSortIndicator("key")}
                </div>
              </TableHead>
              <TableHead
                className="w-1/2 font-medium text-xs py-1 cursor-pointer hover:bg-muted/70"
                onClick={() => requestSort("value")}
              >
                <div className="flex items-center">
                  Value
                  {getSortIndicator("value")}
                </div>
              </TableHead>
              <TableHead className="w-20 font-medium text-xs py-1">
                Secured
              </TableHead>
              <TableHead className="w-20 text-right font-medium text-xs py-1">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="divide-y divide-muted">
            {filteredVariables.length > 0 ? (
              filteredVariables.map((variable) => (
                <VariableRow
                  key={variable.uuid}
                  variable={variable}
                  isSelected={selectedVariables.includes(variable.uuid)}
                  onSelect={onSelectVariable}
                  onEdit={onEditVariable}
                  onDelete={onDeleteVariable}
                  editingVar={editingVar}
                  editValue={editValue}
                  startEditing={startEditing}
                  cancelEditing={cancelEditing}
                  saveEdit={saveEdit}
                  setEditValue={setEditValue}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="h-32 text-center">
                  <div className="text-muted-foreground">
                    No variables match your filter
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default VariableTable;
